import { useEffect, useState } from 'react'
import { useFormikContext } from 'formik'
import { useTranslation } from 'react-i18next'
import type { Gender, PersonNameDto } from '../../../../config/contactConfig'
import { useSettings } from '../../../../hooks/useSettings'
import { getDefaultSettingValue } from '../../../helpers/salutationHelper'

/**
 * The minimal Formik value shape the auto-fill needs. The natural-person mutation DTO satisfies it.
 */
export type SalutationFormValues = {
    gender?: Gender
    name?: PersonNameDto
    salutation?: string
    attentionOf?: string
}

export type SalutationAutoFill = {
    /** Call from the salutation field's onBlur/onKeyUp to (un)mark it as manually edited. */
    onSalutationEdited: () => void
    /** Call from the attention-of field's onBlur/onKeyUp to (un)mark it as manually edited. */
    onAttentionOfEdited: () => void
}

/**
 * Keeps "aanhef" (salutation) and "t.a.v." (attentionOf) automatically in sync with the name/gender
 * fields, until the user edits either field by hand — after which that field stops auto-updating.
 *
 * Lifted verbatim from the legacy NaturalPersonForm so behaviour (HAM-3911) is preserved: the
 * "custom" flag is seeded from the initial values (a stored value differing from the computed
 * default counts as manual), and recomputed on every name/gender/language change.
 */
export default function useSalutationAutoFill<T extends SalutationFormValues>(): SalutationAutoFill {
    const { i18n } = useTranslation()
    const { settings } = useSettings()
    const { initialValues, values, setFieldValue } = useFormikContext<T>()

    // undefined = not yet initialised from the loaded settings/initial values
    const [hasCustomSalutation, setHasCustomSalutation] = useState<boolean>(undefined)
    const [hasCustomAttentionOf, setHasCustomAttentionOf] = useState<boolean>(undefined)

    const isCustomSalutation = (v: SalutationFormValues): boolean =>
        v.salutation?.trim().length > 0 && v.salutation.trim() !== getDefaultSettingValue(v, i18n.language, settings?.partySalutation)

    const isCustomAttentionOf = (v: SalutationFormValues): boolean =>
        v.attentionOf?.trim().length > 0 && v.attentionOf.trim() !== getDefaultSettingValue(v, i18n.language, settings?.partyAttentionOf)

    // Seed the "manually edited" flags once the settings (and thus the computable default) are known.
    useEffect(() => {
        setHasCustomSalutation(isCustomSalutation(initialValues))
        setHasCustomAttentionOf(isCustomAttentionOf(initialValues))
    }, [settings])

    useEffect(() => {
        if (hasCustomSalutation === false) {
            const next = getDefaultSettingValue(values, i18n.language, settings?.partySalutation)
            // Skip no-op sets (e.g. '' over an undefined field on mount) so a pristine form doesn't
            // become dirty and trip the unsaved-changes guard without any user input.
            if ((values.salutation ?? '') !== next) {
                setFieldValue('salutation', next)
            }
        }
    }, [values.name, values.gender, i18n.language, hasCustomSalutation])

    useEffect(() => {
        if (hasCustomAttentionOf === false) {
            const next = getDefaultSettingValue(values, i18n.language, settings?.partyAttentionOf)
            if ((values.attentionOf ?? '') !== next) {
                setFieldValue('attentionOf', next)
            }
        }
    }, [values.name, values.gender, i18n.language, hasCustomAttentionOf])

    return {
        onSalutationEdited: () => setHasCustomSalutation(isCustomSalutation(values)),
        onAttentionOfEdited: () => setHasCustomAttentionOf(isCustomAttentionOf(values)),
    }
}
